import { useId, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  can,
  INSTALL_PLUGIN_PERMISSION,
  PUBLISH_PLUGIN_PERMISSION,
  REVIEW_PLUGIN_PERMISSION,
  RUN_PLUGIN_PERMISSION,
  SUBMIT_PLUGIN_PERMISSION,
  useIdentity,
} from '@/entities/session';
import { useAuditEntries } from '@/features/audit';
import { useDatasetNames } from '@/features/catalog-browser';
import type {
  Capability,
  Installation,
  Plugin,
  PluginRun,
  PluginState,
  RunStatus,
} from '@/shared/api/generated/marketplace';
import { ageText } from '@/shared/model/age';
import { personName } from '@/shared/model/person';
import { Button } from '@/shared/ui/Button';
import { HelpLabel } from '@/shared/ui/InfoTip';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import {
  REGISTRY_LIMIT,
  useCapabilityCatalogue,
  useInstallations,
  useMyListings,
  usePlugins,
  usePublishPlugin,
  useReviewPlugin,
  useRunFindings,
  useRuns,
  useSetInstallationEnabled,
  useSubmitPlugin,
  useUninstallPlugin,
} from '../api/marketplace';
import {
  capabilityLabelKey,
  groupByName,
  installationFor,
  isTerminal,
  isThinRun,
  matchesQuery,
  runBlockedReason,
  TAB_IDS,
  upgradeTargetFor,
} from '../model/marketplace';
import type { TabId } from '../model/marketplace';
import { ListingDrawer } from './ListingDrawer';
import { PluginDrawer } from './PluginDrawer';
import type { DrawerStart } from './PluginDrawer';

/* The marketplace, as the design draws it: six tabs over one registry.
 *
 * **Discovery is a grid of cards, one per plugin name**, carrying the newest version, its state,
 * what it declares and whether this tenant has installed it. A card opens the plugin's panel, where
 * the capabilities to grant are chosen. The sandbox boundary sits under the grid in one card: the
 * three capabilities that exist, each with the permission that gates it on hover, and row values
 * marked never.
 *
 * **Nothing here holds a value from a customer's table.** A plugin sees only what a tenant grants
 * it, and the three capabilities are metadata — schema, scores, rule results.
 */

export interface Permissions {
  readonly submit: boolean;
  readonly review: boolean;
  readonly publish: boolean;
  readonly install: boolean;
  readonly run: boolean;
}

const CAPABILITIES: readonly Capability[] = [
  'dataset.schema',
  'quality.score',
  'rule.results',
];

/** A capability's short name — "Dataset schema" — for a chip. */
function capabilityName(code: Capability): string {
  return `marketplace.capName.${code.replace('.', '_')}`;
}

const STATE_TONE = {
  submitted: 'info',
  reviewed: 'lav',
  approved: 'ok',
  published: 'ok',
  rejected: 'error',
  revoked: 'muted',
} satisfies Record<PluginState, string>;

const RUN_STATUS_TONE = {
  succeeded: 'ok',
  failed: 'error',
  refused: 'warn',
} satisfies Record<RunStatus, string>;

function StatePill({ state }: { readonly state: PluginState }): ReactElement {
  const { t } = useTranslation();
  return (
    <span className="lq-mk__pill" data-tone={STATE_TONE[state]}>
      <span className="lq-mk__pillDot" aria-hidden="true" />
      {t(`marketplace.state.${state}`)}
    </span>
  );
}

interface Opened {
  readonly id: string;
  readonly start: DrawerStart;
}

export function MarketplaceScreen(): ReactElement {
  const { t } = useTranslation();
  const identity = useIdentity();
  const [tab, setTab] = useState<TabId>('marketplace');
  const [openPlugin, setOpenPlugin] = useState<Opened | null>(null);
  const [openListing, setOpenListing] = useState<string | null>(null);
  const tabId = useId();

  const plugins = usePlugins();
  const installations = useInstallations();
  const catalogue = useCapabilityCatalogue();

  const may: Permissions = {
    submit: can(identity.data, SUBMIT_PLUGIN_PERMISSION),
    review: can(identity.data, REVIEW_PLUGIN_PERMISSION),
    publish: can(identity.data, PUBLISH_PLUGIN_PERMISSION),
    install: can(identity.data, INSTALL_PLUGIN_PERMISSION),
    run: can(identity.data, RUN_PLUGIN_PERMISSION),
  };

  /* A deployment with no plugin runtime answers 404 for the registry, and the router is not
     mounted at all: the honest thing to say is that there is no runtime, not that a read failed. */
  const noRuntime =
    plugins.isError &&
    /404|not found/i.test((plugins.error as Error | null)?.message ?? '');

  const open = (id: string, start: DrawerStart = 'view'): void => {
    setOpenListing(null);
    setOpenPlugin({ id, start });
  };

  return (
    <div className="lq-mk">
      <header>
        <HelpLabel subject={t('marketplace.title')} help={t('help.feature.marketplace')}>
          <h1 className="lq-mk__h1">{t('marketplace.title')}</h1>
        </HelpLabel>
        <p className="lq-mk__lede">{t('marketplace.lede')}</p>
      </header>

      <div
        className="lq-mk__tabs"
        role="tablist"
        aria-label={t('marketplace.tabs.label')}
      >
        {TAB_IDS.map((id) => (
          <button
            key={id}
            id={`${tabId}-${id}`}
            type="button"
            role="tab"
            className="lq-mk__tab"
            aria-selected={id === tab}
            onClick={() => {
              setTab(id);
              setOpenPlugin(null);
              setOpenListing(null);
            }}
          >
            {t(`marketplace.tab.${id}`)}
          </button>
        ))}
      </div>

      <div role="tabpanel" aria-labelledby={`${tabId}-${tab}`}>
        {noRuntime ? (
          <p className="lq-mk__noRuntime">{t('marketplace.noRuntime.short')}</p>
        ) : plugins.isPending ? (
          <SkeletonRows rows={4} />
        ) : (
          <>
            {tab === 'marketplace' ? (
              <Discovery
                plugins={plugins.data ?? []}
                failed={plugins.isError}
                installations={installations.data ?? []}
                catalogue={catalogue}
                onOpen={open}
              />
            ) : null}
            {tab === 'installed' ? (
              <Installed
                plugins={plugins.data ?? []}
                installations={installations}
                may={may}
                onOpen={open}
              />
            ) : null}
            {tab === 'runs' ? <Runs /> : null}
            {tab === 'listings' ? <Listings may={may} onOpen={setOpenListing} /> : null}
            {tab === 'review' ? (
              <ReviewQueue
                plugins={plugins.data ?? []}
                may={may}
                onOpen={(id) => {
                  setOpenPlugin(null);
                  setOpenListing(id);
                }}
              />
            ) : null}
            {tab === 'activity' ? <Activity plugins={plugins.data ?? []} /> : null}
          </>
        )}
      </div>

      {openPlugin !== null ? (
        <PluginDrawer
          key={`${openPlugin.id}:${openPlugin.start}`}
          pluginId={openPlugin.id}
          start={openPlugin.start}
          may={may}
          onClose={() => {
            setOpenPlugin(null);
          }}
        />
      ) : null}
      {openListing !== null ? (
        <ListingDrawer
          key={openListing}
          pluginId={openListing}
          may={may}
          onClose={() => {
            setOpenListing(null);
          }}
        />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ discovery */

function Discovery({
  plugins,
  failed,
  installations,
  catalogue,
  onOpen,
}: {
  readonly plugins: readonly Plugin[];
  readonly failed: boolean;
  readonly installations: readonly Installation[];
  readonly catalogue: ReturnType<typeof useCapabilityCatalogue>;
  readonly onOpen: (id: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  /* Published and revoked only: a submission still in review is not something a tenant can
     install, and a rejected one never will be. A revoked plugin stays so a tenant that installed it
     can see why it stopped. */
  const listed = plugins.filter(
    (each) =>
      (each.state === 'published' || each.state === 'revoked') &&
      matchesQuery(each, query),
  );
  const groups = groupByName(listed);

  return (
    <div className="lq-mk__stack">
      {failed ? <p className="lq-mk__error">{t('marketplace.error.registry')}</p> : null}

      <input
        className="lq-input lq-mk__search"
        type="search"
        value={query}
        placeholder={t('marketplace.search.placeholder')}
        aria-label={t('marketplace.search.label')}
        onChange={(event) => {
          setQuery(event.target.value);
        }}
      />

      {plugins.length === REGISTRY_LIMIT ? (
        <p className="lq-mk__quiet">
          {t('marketplace.registryTruncated', { limit: REGISTRY_LIMIT })}
        </p>
      ) : null}

      {groups.length === 0 ? (
        <p className="lq-mk__none">{t('marketplace.search.noneShort')}</p>
      ) : (
        <div className="lq-mk__grid">
          {groups.map((group) => {
            const [top] = group.versions;
            if (top === undefined) return null;
            const installed = group.versions.find(
              (version) => installationFor(version, installations) !== undefined,
            );
            return (
              <button
                key={group.name}
                type="button"
                className="lq-mk__card"
                onClick={() => {
                  onOpen((installed ?? top).id);
                }}
              >
                <span className="lq-mk__cardTop">
                  <span className="lq-mk__name">{group.name}</span>
                  <StatePill state={top.state} />
                </span>
                <span className="lq-mk__meta">
                  {t('marketplace.card.meta', {
                    version: top.version,
                    count: group.versions.length,
                  })}{' '}
                  <span className="lq-mono" title={top.artifact_hash}>
                    {top.artifact_hash.slice(0, 19)}…
                  </span>
                </span>
                <span className="lq-mk__chips">
                  {top.declared_capabilities.map((code) => (
                    <span key={code} className="lq-mk__chip">
                      {t(capabilityName(code))}
                    </span>
                  ))}
                </span>
                {isTerminal(top.state) ? (
                  <span className="lq-mk__meta">
                    {t(`marketplace.terminal.${top.state}`)}
                  </span>
                ) : null}
                {installed !== undefined ? (
                  <span className="lq-mk__installed">
                    {t('marketplace.card.installed')}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}

      <section
        className="lq-card lq-mk__boundary"
        aria-label={t('marketplace.boundary.title')}
      >
        <div className="lq-mk__boundaryText">
          <div className="lq-mk__boundaryTitle">{t('marketplace.boundary.title')}</div>
          <div className="lq-mk__boundaryBody">{t('marketplace.boundary.short')}</div>
        </div>
        <div className="lq-mk__chips">
          {CAPABILITIES.map((code) => (
            <span
              key={code}
              className="lq-mk__chip"
              data-tone="ok"
              /* The permission that gates the capability, as the deployment states it. On hover
                 rather than printed, so the card stays one line of facts. */
              title={
                catalogue.data?.capabilities.find((entry) => entry.code === code)
                  ?.governed_by
              }
            >
              {t(capabilityName(code))}
            </span>
          ))}
          <span className="lq-mk__chip" data-tone="error">
            {t('marketplace.boundary.rowValues')}
          </span>
        </div>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ installed */

function Installed({
  plugins,
  installations,
  may,
  onOpen,
}: {
  readonly plugins: readonly Plugin[];
  readonly installations: ReturnType<typeof useInstallations>;
  readonly may: Permissions;
  readonly onOpen: (id: string, start?: DrawerStart) => void;
}): ReactElement {
  const { t } = useTranslation();
  const setEnabled = useSetInstallationEnabled();
  const uninstall = useUninstallPlugin();
  const [confirming, setConfirming] = useState<string | null>(null);
  const [now] = useState(() => new Date());

  if (installations.isError) {
    return <p className="lq-mk__error">{t('marketplace.error.installations')}</p>;
  }
  if (installations.isPending) return <SkeletonRows rows={3} />;
  const rows = installations.data;

  return (
    <div className="lq-mk__stack">
      {setEnabled.isError ? (
        <p className="lq-mk__error">{setEnabled.error.message}</p>
      ) : null}
      {uninstall.isError ? (
        <p className="lq-mk__error">{uninstall.error.message}</p>
      ) : null}

      {rows.length === 0 ? (
        <p className="lq-mk__none">{t('marketplace.installed.none')}</p>
      ) : null}

      {rows.map((installation) => {
        const plugin = plugins.find(
          (candidate) => candidate.id === installation.plugin_id,
        );
        const upgrade =
          plugin === undefined ? undefined : upgradeTargetFor(plugin, plugins);
        const runReason = runBlockedReason(plugin, installation);
        const name = plugin?.name ?? t('marketplace.installed.unknownPlugin');
        return (
          <article className="lq-card lq-mk__inst" key={installation.plugin_id}>
            <div className="lq-mk__instTop">
              <span className="lq-mk__name">{name}</span>
              {plugin === undefined ? null : (
                <span className="lq-mk__ver">{plugin.version}</span>
              )}
              <span
                className="lq-mk__pill"
                data-tone={installation.enabled ? 'ok' : 'muted'}
              >
                {installation.enabled
                  ? t('marketplace.install.enabled')
                  : t('marketplace.install.disabled')}
              </span>
              <span className="lq-mk__spacer" />
              <span
                className="lq-mk__meta"
                title={new Date(installation.installed_at).toLocaleString()}
              >
                {personName(installation.installed_by)} ·{' '}
                {ageText(t, installation.installed_at, now) ?? installation.installed_at}
              </span>
            </div>
            <div className="lq-mk__chips">
              <span className="lq-mk__meta">{t('marketplace.installed.granted')}</span>
              {installation.granted_capabilities.map((code) => (
                <span key={code} className="lq-mk__chip">
                  {t(capabilityName(code))}
                </span>
              ))}
            </div>
            {plugin?.state === 'revoked' ? (
              <p className="lq-mk__revoked">{t('marketplace.installed.revoked')}</p>
            ) : null}
            <div className="lq-mk__actions">
              <Button
                size="compact"
                disabled={!may.run || runReason !== null}
                title={runReason === null ? undefined : t(runReason)}
                onClick={() => {
                  onOpen(installation.plugin_id, 'run');
                }}
              >
                {t('marketplace.action.runOnDataset')}
              </Button>
              <Button
                size="compact"
                disabled={!may.install || setEnabled.isPending}
                onClick={() => {
                  setEnabled.mutate({
                    pluginId: installation.plugin_id,
                    enabled: !installation.enabled,
                  });
                }}
              >
                {installation.enabled
                  ? t('marketplace.action.disable')
                  : t('marketplace.action.enable')}
              </Button>
              {upgrade !== undefined ? (
                <Button
                  size="compact"
                  disabled={!may.install}
                  onClick={() => {
                    onOpen(installation.plugin_id, 'upgrade');
                  }}
                >
                  {t('marketplace.action.upgradeTo', { version: upgrade.version })}
                </Button>
              ) : null}
              <Button
                size="compact"
                disabled={!may.install}
                onClick={() => {
                  setConfirming(installation.plugin_id);
                }}
              >
                {t('marketplace.action.uninstall')}
              </Button>
            </div>
            {confirming === installation.plugin_id ? (
              <div className="lq-mk__confirm">
                <span className="lq-mk__spacer">
                  {t('marketplace.uninstall.ask', { name })}
                </span>
                <Button
                  size="compact"
                  onClick={() => {
                    setConfirming(null);
                  }}
                >
                  {t('marketplace.action.keep')}
                </Button>
                <Button
                  size="compact"
                  tone="danger"
                  busy={uninstall.isPending}
                  onClick={() => {
                    uninstall.mutate(installation.plugin_id, {
                      onSuccess: () => {
                        setConfirming(null);
                      },
                    });
                  }}
                >
                  {t('marketplace.action.uninstall')}
                </Button>
              </div>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ runs */

function Runs(): ReactElement {
  const { t } = useTranslation();
  const runs = useRuns(null);
  const names = useDatasetNames();
  const [openRun, setOpenRun] = useState<PluginRun | null>(null);
  const [now] = useState(() => new Date());

  if (openRun !== null) {
    return (
      <RunDetail
        run={openRun}
        dataset={names.data?.byId.get(openRun.dataset_id) ?? null}
        onBack={() => {
          setOpenRun(null);
        }}
      />
    );
  }
  if (runs.isError) return <p className="lq-mk__error">{t('marketplace.error.runs')}</p>;
  if (runs.isPending) return <SkeletonRows rows={4} />;

  return (
    <div className="lq-card lq-mk__tableCard">
      <table className="lq-mk__table" aria-label={t('marketplace.runs.caption')}>
        <thead>
          <tr>
            <th scope="col">{t('marketplace.runs.plugin')}</th>
            <th scope="col">{t('marketplace.runs.dataset')}</th>
            <th scope="col">{t('marketplace.runs.status')}</th>
            <th scope="col">{t('marketplace.runs.result')}</th>
            <th scope="col">{t('marketplace.runs.used')}</th>
            <th scope="col">{t('marketplace.runs.by')}</th>
            <th scope="col">{t('marketplace.runs.when')}</th>
          </tr>
        </thead>
        <tbody>
          {runs.data.map((run) => (
            <tr key={run.id}>
              <td data-label={t('marketplace.runs.plugin')} className="lq-mk__mono">
                {/* The row opens the run's findings. A button in the first cell rather than a
                    clickable row, so the table stays a table to a screen reader. */}
                <button
                  type="button"
                  className="lq-mk__rowLink"
                  onClick={() => {
                    setOpenRun(run);
                  }}
                >
                  {run.plugin_name} {run.plugin_version}
                </button>
              </td>
              <td
                data-label={t('marketplace.runs.dataset')}
                className="lq-mk__mono lq-mk__thin"
              >
                <span title={run.dataset_id}>
                  {names.data?.byId.get(run.dataset_id) ?? '—'}
                </span>
              </td>
              <td data-label={t('marketplace.runs.status')}>
                <span className="lq-mk__pill" data-tone={RUN_STATUS_TONE[run.status]}>
                  {t(`marketplace.runStatus.${run.status}`)}
                </span>
              </td>
              <td data-label={t('marketplace.runs.result')} className="lq-mk__dim">
                {run.status === 'succeeded'
                  ? t('marketplace.runs.findings', { count: run.findings_count })
                  : run.failure_reason === null
                    ? '—'
                    : t(`marketplace.failure.${run.failure_reason}`)}
              </td>
              <td data-label={t('marketplace.runs.used')} className="lq-mk__meta">
                {run.capabilities_used.length === 0
                  ? t('marketplace.runs.usedNone')
                  : run.capabilities_used
                      .map((code) => t(capabilityName(code)))
                      .join(', ')}
              </td>
              <td data-label={t('marketplace.runs.by')}>{personName(run.actor)}</td>
              <td
                data-label={t('marketplace.runs.when')}
                className="lq-mk__meta lq-nowrap"
              >
                {ageText(t, run.ran_at, now) ?? run.ran_at} ·{' '}
                {t('marketplace.runs.duration', { ms: run.duration_ms })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {runs.data.length === 0 ? (
        <p className="lq-mk__none">{t('marketplace.runs.none')}</p>
      ) : null}
    </div>
  );
}

function RunDetail({
  run,
  dataset,
  onBack,
}: {
  readonly run: PluginRun;
  readonly dataset: string | null;
  readonly onBack: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const findings = useRunFindings(run.id);

  return (
    <div className="lq-mk__stack">
      <button type="button" className="lq-mk__back" onClick={onBack}>
        {t('marketplace.run.back')}
      </button>
      <article className="lq-card lq-mk__inst">
        <div className="lq-mk__instTop">
          <span className="lq-mk__name">
            {run.plugin_name} {run.plugin_version}
          </span>
          <span className="lq-mk__pill" data-tone={RUN_STATUS_TONE[run.status]}>
            {t(`marketplace.runStatus.${run.status}`)}
          </span>
        </div>
        <p className="lq-mk__meta">
          {t('marketplace.run.line', {
            dataset: dataset ?? run.dataset_id,
            who: personName(run.actor),
            when: new Date(run.ran_at).toLocaleString(),
            ms: run.duration_ms,
          })}
        </p>
        {isThinRun(run) ? (
          <p className="lq-mk__meta">{t('marketplace.run.thinBody')}</p>
        ) : null}
        {run.failure_reason !== null ? (
          <p className="lq-mk__error">{t(`marketplace.failure.${run.failure_reason}`)}</p>
        ) : null}
        {findings.isError ? (
          <p className="lq-mk__meta">{t('marketplace.run.findingsUnread')}</p>
        ) : null}
        {findings.data?.length === 0 ? (
          <p className="lq-mk__meta">{t('marketplace.run.noFindings')}</p>
        ) : null}
        {(findings.data ?? []).map((finding, index) => (
          <div className="lq-mk__finding" key={`${finding.code}-${String(index)}`}>
            {/* Severity is a free-form string the plugin chose, rendered as given rather than
                mapped onto a platform severity it does not mean. */}
            <span className="lq-mk__chip">{finding.severity}</span>
            <span className="lq-mk__mono">{finding.code}</span>
            <span>{finding.message}</span>
          </div>
        ))}
        {run.findings_truncated ? (
          <p className="lq-mk__meta">
            {t('marketplace.run.truncated', { count: run.findings_count })}
          </p>
        ) : null}
      </article>
    </div>
  );
}

/* ------------------------------------------------------------------ my listings */

function Listings({
  may,
  onOpen,
}: {
  readonly may: Permissions;
  readonly onOpen: (id: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const listings = useMyListings();
  const [open, setOpen] = useState(false);

  return (
    <div className="lq-mk__stack">
      <div className="lq-mk__listHead">
        <span className="lq-mk__listTitle">{t('marketplace.listings.title')}</span>
        <Button
          tone="primary"
          disabled={!may.submit}
          title={
            may.submit
              ? undefined
              : t('marketplace.needs', { permission: SUBMIT_PLUGIN_PERMISSION })
          }
          onClick={() => {
            setOpen((was) => !was);
          }}
        >
          {t('marketplace.action.submitPlugin')}
        </Button>
      </div>

      {open ? (
        <SubmitForm
          onDone={() => {
            setOpen(false);
          }}
        />
      ) : null}

      {listings.isError ? (
        <p className="lq-mk__error">{t('marketplace.error.listings')}</p>
      ) : null}
      {listings.isPending ? <SkeletonRows rows={2} /> : null}
      {listings.data?.length === 0 ? (
        <p className="lq-mk__none">{t('marketplace.listings.none')}</p>
      ) : null}

      {(listings.data ?? []).map((listing) => (
        <button
          type="button"
          className="lq-card lq-mk__mine"
          key={listing.id}
          onClick={() => {
            onOpen(listing.id);
          }}
        >
          <span className="lq-mk__name lq-mk__name--sm">{listing.name}</span>
          <span className="lq-mk__ver">{listing.version}</span>
          <StatePill state={listing.state} />
          {isTerminal(listing.state) ? (
            <span className="lq-mk__meta lq-mk__full">
              {t(`marketplace.terminal.${listing.state}`)}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

function SubmitForm({ onDone }: { readonly onDone: () => void }): ReactElement {
  const { t } = useTranslation();
  const submit = useSubmitPlugin();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [name, setName] = useState('');
  const [version, setVersion] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [artifact, setArtifact] = useState<string | null>(null);
  const [caps, setCaps] = useState<readonly Capability[]>([]);
  const [problem, setProblem] = useState<string | null>(null);

  async function read(file: File): Promise<void> {
    setProblem(null);
    setArtifact(null);
    setFileName(null);
    const bytes = new Uint8Array(await file.arrayBuffer());
    /* Refused here as well as on the server: an artifact over the cap or not a WebAssembly module
       would be refused by the review, and saying so before the upload saves the round trip. */
    if (bytes.byteLength > 8 * 1024 * 1024) {
      setProblem(t('marketplace.submit.tooLarge'));
      return;
    }
    if (!(
      bytes[0] === 0x00 &&
      bytes[1] === 0x61 &&
      bytes[2] === 0x73 &&
      bytes[3] === 0x6d
    )) {
      setProblem(t('marketplace.submit.notWasm'));
      return;
    }
    let binary = '';
    for (const byte of bytes) binary += String.fromCharCode(byte);
    setArtifact(btoa(binary));
    setFileName(file.name);
  }

  function send(): void {
    if (!/^[a-z0-9-]{2,60}$/u.test(name.trim())) {
      setProblem(t('marketplace.submit.error.name'));
      return;
    }
    if (!/^\d+\.\d+\.\d+$/u.test(version.trim())) {
      setProblem(t('marketplace.submit.error.version'));
      return;
    }
    if (artifact === null) {
      setProblem(t('marketplace.submit.error.file'));
      return;
    }
    if (caps.length === 0) {
      setProblem(t('marketplace.submit.error.caps'));
      return;
    }
    submit.mutate(
      { name: name.trim(), version: version.trim(), artifact, capabilities: caps },
      { onSuccess: onDone },
    );
  }

  return (
    <article className="lq-card lq-mk__form">
      <div className="lq-mk__formGrid">
        <label className="lq-mk__field">
          {t('marketplace.submit.name')}
          <input
            className="lq-input"
            value={name}
            placeholder="gstin-checksum"
            onChange={(event) => {
              setName(event.target.value);
              setProblem(null);
            }}
          />
        </label>
        <label className="lq-mk__field">
          {t('marketplace.submit.version')}
          <input
            className="lq-input"
            value={version}
            placeholder="1.0.0"
            onChange={(event) => {
              setVersion(event.target.value);
              setProblem(null);
            }}
          />
        </label>
      </div>
      <div className="lq-mk__fileRow">
        <input
          ref={fileRef}
          hidden
          type="file"
          accept=".wasm"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file !== undefined) void read(file);
          }}
        />
        <Button
          size="compact"
          onClick={() => {
            fileRef.current?.click();
          }}
        >
          {t('marketplace.submit.choose')}
        </Button>
        <span className={fileName === null ? 'lq-mk__meta' : 'lq-mk__fileName'}>
          {fileName ?? t('marketplace.submit.noFile')}
        </span>
        <span className="lq-mk__meta">{t('marketplace.submit.cap')}</span>
      </div>
      <fieldset className="lq-mk__caps">
        <legend className="lq-mk__legend">{t('marketplace.submit.declared')}</legend>
        {CAPABILITIES.map((code) => (
          <label className="lq-mk__capCheck" key={code}>
            <input
              type="checkbox"
              checked={caps.includes(code)}
              onChange={(event) => {
                setProblem(null);
                setCaps(
                  event.target.checked
                    ? [...caps, code]
                    : caps.filter((each) => each !== code),
                );
              }}
            />
            <span>
              <strong className="lq-mk__capName">{t(capabilityName(code))}</strong>
              <span className="lq-mk__meta"> — {t(capabilityLabelKey(code))}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {problem !== null ? (
        <p role="alert" className="lq-mk__error">
          {problem}
        </p>
      ) : null}
      {submit.isError ? (
        <p role="alert" className="lq-mk__error">
          {submit.error.message}
        </p>
      ) : null}
      <div className="lq-mk__actions">
        <Button size="compact" onClick={onDone}>
          {t('marketplace.action.cancel')}
        </Button>
        <Button size="compact" tone="primary" busy={submit.isPending} onClick={send}>
          {t('marketplace.submit.go')}
        </Button>
      </div>
    </article>
  );
}

/* ------------------------------------------------------------------ review */

function ReviewQueue({
  plugins,
  may,
  onOpen,
}: {
  readonly plugins: readonly Plugin[];
  readonly may: Permissions;
  readonly onOpen: (id: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const review = useReviewPlugin();
  const publish = usePublishPlugin();

  const awaitingReview = plugins.filter((each) => each.state === 'submitted');
  /* `reviewed` waits for two approvals; `approved` has them and waits for somebody to publish. */
  const awaitingPublication = plugins.filter(
    (each) => each.state === 'reviewed' || each.state === 'approved',
  );

  return (
    <div className="lq-mk__review">
      <article className="lq-card lq-mk__queue">
        <h2 className="lq-mk__queueTitle">{t('marketplace.review.awaitingReview')}</h2>
        {review.isError ? <p className="lq-mk__error">{review.error.message}</p> : null}
        {awaitingReview.map((plugin) => {
          const busy = review.isPending && review.variables === plugin.id;
          return (
            <div className="lq-mk__queueItem" key={plugin.id}>
              <span className="lq-mk__name lq-mk__name--sm">
                {plugin.name} {plugin.version}
              </span>
              <span className="lq-mk__hash" title={plugin.artifact_hash}>
                {plugin.artifact_hash.slice(0, 19)}…
              </span>
              <Button
                size="compact"
                className="lq-mk__start"
                disabled={!may.review || review.isPending}
                title={
                  may.review
                    ? undefined
                    : t('marketplace.needs', { permission: REVIEW_PLUGIN_PERMISSION })
                }
                onClick={() => {
                  review.mutate(plugin.id);
                }}
              >
                {busy ? t('marketplace.review.running') : t('marketplace.review.run')}
              </Button>
              <span className="lq-mk__fine">
                {t('marketplace.review.sandboxDecides')}
              </span>
            </div>
          );
        })}
        {awaitingReview.length === 0 ? (
          <p className="lq-mk__meta">{t('marketplace.review.nothing')}</p>
        ) : null}
      </article>

      <article className="lq-card lq-mk__queue">
        <h2 className="lq-mk__queueTitle">
          {t('marketplace.review.awaitingPublication')}
        </h2>
        {publish.isError ? <p className="lq-mk__error">{publish.error.message}</p> : null}
        {awaitingPublication.map((plugin) => (
          <div className="lq-mk__queueItem" key={plugin.id}>
            <span className="lq-mk__queueTop">
              <span className="lq-mk__name lq-mk__name--sm">
                {plugin.name} {plugin.version}
              </span>
              <StatePill state={plugin.state} />
            </span>
            <span className="lq-mk__dim">
              {t('marketplace.review.verdictLine', { verdict: plugin.review_verdict })}
            </span>
            <span className="lq-mk__actions">
              {/* Approving states a reason, which the plugin's panel asks for — so Approve opens
                  it rather than signing with no reason. */}
              {plugin.state === 'reviewed' ? (
                <Button
                  size="compact"
                  disabled={!may.publish}
                  title={
                    may.publish
                      ? undefined
                      : t('marketplace.needs', { permission: PUBLISH_PLUGIN_PERMISSION })
                  }
                  onClick={() => {
                    onOpen(plugin.id);
                  }}
                >
                  {t('marketplace.action.approve')}
                </Button>
              ) : null}
              {plugin.state === 'approved' ? (
                <Button
                  size="compact"
                  disabled={!may.publish}
                  busy={publish.isPending && publish.variables === plugin.id}
                  onClick={() => {
                    publish.mutate(plugin.id);
                  }}
                >
                  {t('marketplace.action.publish')}
                </Button>
              ) : null}
            </span>
          </div>
        ))}
        {awaitingPublication.length === 0 ? (
          <p className="lq-mk__meta">{t('marketplace.review.nothing')}</p>
        ) : null}
      </article>
    </div>
  );
}

/* ------------------------------------------------------------------ activity */

function Activity({ plugins }: { readonly plugins: readonly Plugin[] }): ReactElement {
  const { t } = useTranslation();
  const activity = useAuditEntries({ aggregateType: 'Plugin' }, null, 50);
  const [now] = useState(() => new Date());

  if (activity.isError)
    return <p className="lq-mk__error">{t('marketplace.error.activity')}</p>;
  if (activity.isPending) return <SkeletonRows rows={4} />;
  const items = activity.data.items;

  return (
    <div className="lq-card lq-mk__activity">
      {items.length === 0 ? (
        <p className="lq-mk__none">{t('marketplace.activity.none')}</p>
      ) : null}
      {items.map((entry) => {
        const plugin = plugins.find((each) => each.id === entry.aggregate_id);
        return (
          <div
            className="lq-mk__act"
            key={`${entry.chain_date}-${String(entry.sequence)}`}
          >
            <span className="lq-mk__actKind">
              {t(`marketplace.audit.${entry.action}`, { defaultValue: entry.action })}
            </span>
            <span>
              <span className="lq-mk__mono">
                {plugin === undefined
                  ? `${entry.aggregate_id.slice(0, 8)}…`
                  : `${plugin.name} ${plugin.version}`}
              </span>
              <span className="lq-mk__meta"> · {personName(entry.actor)}</span>
            </span>
            <span
              className="lq-mk__meta"
              title={new Date(entry.occurred_at).toLocaleString()}
            >
              {ageText(t, entry.occurred_at, now) ?? entry.occurred_at}
            </span>
          </div>
        );
      })}
    </div>
  );
}
